import { useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Dayjs } from 'dayjs';
import { DataProvider, Product } from 'types/app.types';
import ProductsContext from 'contexts/ProductsContext';
import { AppDispatch, RootState } from 'services/reduxStore';
import dtnApi from 'services/dtn/api';
import { DTNMetadata } from 'services/dtn/types';
import { utcDayjs } from 'utils';

interface StrategyDeps {
  capabilitiesMap: Record<string, { start: string; end: string } | undefined>;
  dtnMetadataByLayer: Record<string, DTNMetadata | undefined>;
}

type ValidDateRangeStrategy = (
  product: Product,
  deps: StrategyDeps
) => Dayjs[] | null;

const strategies: Record<DataProvider, ValidDateRangeStrategy> = {
  geoserver: (product, { capabilitiesMap }) => {
    const entry = capabilitiesMap[product.internalName];
    if (!entry) return null;
    return [utcDayjs(entry.start), utcDayjs(entry.end)];
  },
  dtn: (product, { dtnMetadataByLayer }) => {
    const metadata = dtnMetadataByLayer[product.internalName];
    if (!metadata?.oldestDataAvailable) return null;
    const endDate = metadata.dataType === 'forecast' ? utcDayjs().add(10, 'day') : utcDayjs();
    return [utcDayjs(metadata.oldestDataAvailable), endDate];
  },
};

function useValidDatesRanges(selectedProducts: Product[]): Dayjs[][] {
  const { capabilities } = useContext(ProductsContext);
  const dispatch = useDispatch<AppDispatch>();

  // --- GeoServer: build a lookup from capabilities ---
  const capabilitiesMap = useMemo(() => {
    const map: Record<string, { start: string; end: string } | undefined> = {};
    if (!capabilities) return map;
    capabilities.Capability.Layer.Layer.forEach((layer) => {
      const timeDimension = layer.Dimension?.find((d) => d.name === 'time');
      if (timeDimension) {
        const [start, end] = timeDimension.values.split('/');
        map[layer.Name] = { start, end };
      }
    });
    return map;
  }, [capabilities]);

  // --- DTN: dispatch metadata queries for each DTN product ---
  const dtnProducts = useMemo(
    () => selectedProducts.filter((p) => p.provider === 'dtn'),
    [selectedProducts],
  );

  useEffect(() => {
    dtnProducts.forEach((product) => {
      dispatch(
        dtnApi.endpoints.getMetadata.initiate({ layerId: product.internalName }),
      );
    });
  }, [dtnProducts, dispatch]);

  const dtnMetadataByLayer = useSelector((state: RootState) => {
    const result: Record<string, DTNMetadata | undefined> = {};
    dtnProducts.forEach((product) => {
      result[product.internalName] = dtnApi.endpoints.getMetadata.select({
        layerId: product.internalName,
      })(state).data;
    });
    return result;
  }, shallowEqual);

  // --- Aggregate ranges using the strategy map ---
  return useMemo(() => {
    const deps: StrategyDeps = { capabilitiesMap, dtnMetadataByLayer };
    return selectedProducts.reduce<Dayjs[][]>((acc, product) => {
      const strategy = strategies[product.provider ?? 'geoserver'];
      const range = strategy?.(product, deps);
      if (range) acc.push(range);
      return acc;
    }, []);
  }, [selectedProducts, capabilitiesMap, dtnMetadataByLayer]);
}

export default useValidDatesRanges;
